/**
 * Slug validation and normalization.
 *
 * A slug becomes BOTH a path (`simplelaunch.io/{slug}`) and a subdomain
 * (`{slug}.simplelaunch.io`), so it must be a single valid DNS label. The
 * wildcard TLS certificate (`*.simplelaunch.io`) covers exactly one label
 * deep, which is why dots are rejected outright: `a.b.simplelaunch.io` would
 * resolve via the DNS wildcard and then fail the TLS handshake.
 *
 * This module has NO dependencies on purpose. It is consumed by the builder
 * SPA, the API Lambdas, and -- via code generation -- the CloudFront viewer
 * function, which runs a restricted JS runtime with no module system.
 */

import RESERVED from './reserved-slugs.json' with { type: 'json' }

export const SLUG_MIN_LENGTH = 3
export const SLUG_MAX_LENGTH = 32

/**
 * Names that can never be claimed by a customer.
 *
 * Three separate hazards live in this one list, which is why it is shared
 * rather than duplicated per call site:
 *   1. DNS/infra names we may need as real subdomains later. A slug that
 *      collides with a future subdomain of ours is unrecoverable once live.
 *   2. Reserved SPA routes on the apex, which must not be shadowed by a
 *      customer page at `simplelaunch.io/{slug}`.
 *   3. Our own brand.
 */
/**
 * Names that can never be claimed by a customer.
 *
 * The list itself lives in reserved-slugs.json because it has three consumers
 * that must never disagree: this validator, the SPA router, and the CloudFront
 * viewer function, which Terraform generates by inlining the same file. A slug
 * that collides with a future subdomain of ours is unrecoverable once it is
 * live, so a drifted copy is a real outage, not a tidiness problem.
 *
 * Three separate hazards are grouped in that file:
 *   1. DNS and infrastructure names we may need as real subdomains later.
 *   2. Reserved SPA routes on the apex, which must not be shadowed by a
 *      customer page at simplelaunch.io/{slug}.
 *   3. Our own brand.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set(
  Object.entries(RESERVED)
    .filter(([key]) => !key.startsWith('_'))
    .flatMap(([, words]) => words as string[]),
)

export type SlugRejection =
  | 'too-short'
  | 'too-long'
  | 'invalid-characters'
  | 'leading-hyphen'
  | 'trailing-hyphen'
  | 'consecutive-hyphens'
  | 'reserved-label-position'
  | 'reserved'

export type SlugCheck =
  | { readonly ok: true; readonly slug: string }
  | { readonly ok: false; readonly reason: SlugRejection }

/** Human-readable copy for each rejection, shown inline in the builder. */
export const SLUG_REJECTION_MESSAGES: Readonly<Record<SlugRejection, string>> = {
  'too-short': `Needs at least ${SLUG_MIN_LENGTH} characters.`,
  'too-long': `Needs to be ${SLUG_MAX_LENGTH} characters or fewer.`,
  'invalid-characters': 'Only lowercase letters, numbers, and hyphens.',
  'leading-hyphen': "Can't start with a hyphen.",
  'trailing-hyphen': "Can't end with a hyphen.",
  'consecutive-hyphens': "Can't contain two hyphens in a row.",
  'reserved-label-position': 'That combination is reserved by the DNS standard.',
  'reserved': 'That name is reserved.',
}

/**
 * Strict validation of a final slug. Never mutates -- use {@link slugify} to
 * derive a candidate, then validate the result.
 */
export function validateSlug(slug: string): SlugCheck {
  if (slug.length < SLUG_MIN_LENGTH) return { ok: false, reason: 'too-short' }
  if (slug.length > SLUG_MAX_LENGTH) return { ok: false, reason: 'too-long' }

  // Checked before the character class so that a leading/trailing hyphen
  // reports the specific problem rather than a generic "invalid characters".
  if (slug.startsWith('-')) return { ok: false, reason: 'leading-hyphen' }
  if (slug.endsWith('-')) return { ok: false, reason: 'trailing-hyphen' }
  if (!/^[a-z0-9-]+$/.test(slug)) return { ok: false, reason: 'invalid-characters' }
  if (slug.includes('--')) {
    // RFC 5891 reserves labels with hyphens in the 3rd and 4th position for
    // IDN (the `xn--` ACE prefix lives here), which is a homograph-spoofing
    // vector. Rejecting all double hyphens is stricter than the RFC and keeps
    // the rule explainable to a founder.
    return {
      ok: false,
      reason: slug[2] === '-' && slug[3] === '-' ? 'reserved-label-position' : 'consecutive-hyphens',
    }
  }
  if (RESERVED_SLUGS.has(slug)) return { ok: false, reason: 'reserved' }

  return { ok: true, slug }
}

export function isValidSlug(slug: string): boolean {
  return validateSlug(slug).ok
}

/**
 * Derive a slug candidate from free text (typically the startup name).
 *
 * Lossy by design: it strips rather than rejects, so typing "Acme Corp!" in
 * the name field immediately suggests "acme-corp". The result still has to
 * pass {@link validateSlug} -- notably, this can return a reserved word or a
 * string that is too short.
 */
export function slugify(input: string): string {
  return input
    .normalize('NFKD')
    // Strip combining marks so accented characters fold to their base letter
    // (é -> e) instead of being dropped entirely.
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/, '') // slice() may have left a trailing hyphen
}
